import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, gql } from 'urql'
import { useAuth } from '../context/auth-provider'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card'
import { Logo } from '../components/logo'

const RESET_PASSWORD_MUTATION = gql(`
  mutation ResetPassword($token: String!, $newPassword: String!) {
    resetPassword(token: $token, newPassword: $newPassword) {
      accessToken
      refreshToken
      user {
        id
        name
        email
      }
    }
  }
`)

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const { setAccessToken } = useAuth()
  const navigate = useNavigate()
  const [{ fetching }, resetPassword] = useMutation(RESET_PASSWORD_MUTATION)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    if (newPassword !== confirm) {
      setError('Passwords do not match.')
      return
    }
    const result = await resetPassword({ token, newPassword })
    if (result.error) {
      setError(result.error.graphQLErrors[0]?.message ?? result.error.message)
      return
    }
    if (result.data?.resetPassword) {
      setAccessToken(result.data.resetPassword.accessToken)
      navigate('/dashboard')
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-primary/5 p-4 gap-8">
        <div className="scale-150">
          <Logo />
        </div>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid link</CardTitle>
            <CardDescription>This password reset link is missing a token.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="link" asChild>
              <Link to="/forgot-password">Request a new link</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-primary/5 dark:bg-gray-900 p-4 gap-8">
      <div className="scale-150">
        <Logo />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Set new password</CardTitle>
          <CardDescription>Choose a new password for your account.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="text-sm text-red-500 font-medium">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="newPassword">New password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <Button type="submit" className="w-full" disabled={fetching}>
              {fetching ? 'Saving...' : 'Set new password'}
            </Button>
            <Button variant="link" size="sm" asChild>
              <Link to="/login">Back to sign in</Link>
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
